import React, { useEffect, useState } from "react";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Campaign } from "@/pages/rewardPoints/interface";
import { Skeleton } from "@/components/skeleton/Skeleton";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { offset, shift } from "@floating-ui/dom";

interface FeaturedCampaignProps {
    onCampaignChange?: (campaign: Campaign | null) => void;
}

const FeaturedCampaignSkeleton = () => (
    <div className="bg-white rounded-2xl p-6 flex flex-col">
        <div className="flex items-center justify-between mb-4">
            <p className="text-xl font-semibold">Bonus Points</p>
            <Skeleton className="h-11 w-32 rounded-full" />
        </div>
        <Skeleton className="aspect-[16/10] rounded-xl mb-4" />
        <Skeleton className="h-5 w-3/4" />
    </div>
);

export const FeaturedCampaign: React.FC<FeaturedCampaignProps> = ({ onCampaignChange }) => {
    const { activeCampaignApi } = API_ROUTES;

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const result = await GET(activeCampaignApi);
                const activeCampaign = result?.data?.campaign ?? null;
                setCampaign(activeCampaign);
                onCampaignChange?.(activeCampaign);
            } catch (error) {
                console.log("get active campaign error:", error);
                setCampaign(null);
                onCampaignChange?.(null);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [activeCampaignApi]);

    if (isLoading) return <FeaturedCampaignSkeleton />;
    if (!campaign) return null;

    return (
        <div className="bg-white rounded-2xl p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <p className="text-xl font-semibold">Bonus Points</p>
                <a
                    href={campaign.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-center bg-primary-red hover:bg-primary-red-button-hover text-white rounded-full py-2 px-5 text-sm"
                >
                    Join
                </a>
            </div>

            <div className="relative rounded-xl overflow-hidden aspect-[16/10] mb-4 bg-p-gray flex-shrink-0">
                <img
                    src={campaign.image_url}
                    alt={campaign.name}
                    className="w-full h-full object-cover"
                />
                <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-primary-red text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                    <span className="w-4 h-4 rounded-full bg-white/25 flex items-center justify-center text-[10px]">
                        P
                    </span>
                    +{campaign.points} pts
                </span>
            </div>

            <p
                className="text-lg font-semibold text-gray-900 line-clamp-1 flex-shrink-0"
                data-tooltip-id="featured-campaign-name"
            >
                {campaign.name}
            </p>
            <ReactTooltip
                id="featured-campaign-name"
                place="left"
                content={campaign.name}
                middlewares={[offset(8), shift({ padding: 8 })]}
                style={{ maxWidth: 400, whiteSpace: "normal", wordBreak: "break-word" }}
            />
        </div>
    );
};
